import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ClockSize = 'sm' | 'md' | 'lg';

interface AppSettings {
  appLogo: string | null;
  appCoverImage: string | null;
  appName: string;
  showPremiumButton: boolean;
  showProfessionalButton: boolean;
  showPricingPage: boolean;
  showPrices: boolean;
  showAnnouncementsButton: boolean;
  showNotificationsButton: boolean;
  // Radio settings
  radioEnabled: boolean;
  radioUrl: string | null;
  radioEmbedCode: string | null;
  radioAutoplay: boolean;
  radioVisibleForUsers: boolean;
  // Streak settings
  streakEnabled: boolean;
  streakVisibleForUsers: boolean;
  streakShowWelcomeModal: boolean;
  // Clock settings
  loginClockEnabled: boolean;
  loginClockSize: ClockSize;
  dashboardClockEnabled: boolean;
  dashboardClockSize: ClockSize;
  // Adaptive learning settings
  adaptiveRecommendationsEnabled: boolean;
  showPostQuizSuggestions: boolean;
  inactivityReminderDays: number;
  // Shop / Academy visibility
  shopEnabled: boolean;
  academyEnabled: boolean;
}

export const useAppSettings = () => {
  const [settings, setSettings] = useState<AppSettings>({
    appLogo: null,
    appCoverImage: null,
    appName: "MusicQuizizz",
    showPremiumButton: true,
    showProfessionalButton: true,
    showPricingPage: true,
    showPrices: false,
    showAnnouncementsButton: true,
    showNotificationsButton: true,
    radioEnabled: false,
    radioUrl: null,
    radioEmbedCode: null,
    radioAutoplay: false,
    radioVisibleForUsers: false,
    streakEnabled: false,
    streakVisibleForUsers: false,
    streakShowWelcomeModal: false,
    loginClockEnabled: true,
    loginClockSize: 'sm',
    dashboardClockEnabled: true,
    dashboardClockSize: 'md',
    adaptiveRecommendationsEnabled: true,
    showPostQuizSuggestions: true,
    inactivityReminderDays: 3,
    shopEnabled: false,
    academyEnabled: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("app_settings")
        .select("setting_key, setting_value");

      if (error) throw error;

      const settingsMap: Record<string, string | null> = {};
      data?.forEach((item) => {
        settingsMap[item.setting_key] = item.setting_value;
      });

      setSettings({
        appLogo: settingsMap["app_logo"] || null,
        appCoverImage: settingsMap["app_cover_image"] || null,
        appName: settingsMap["app_name"] || "MusicQuizizz",
        showPremiumButton: settingsMap["show_premium_button"] !== "false",
        showProfessionalButton: settingsMap["show_professional_button"] !== "false",
        showPricingPage: settingsMap["show_pricing_page"] !== "false",
        showPrices: settingsMap["show_prices"] === "true",
        showAnnouncementsButton: settingsMap["show_announcements_button"] !== "false",
        showNotificationsButton: settingsMap["show_notifications_button"] !== "false",
        radioEnabled: settingsMap["radio_enabled"] === "true",
        radioUrl: settingsMap["radio_url"] || null,
        radioEmbedCode: settingsMap["radio_embed_code"] || null,
        radioAutoplay: settingsMap["radio_autoplay"] === "true",
        radioVisibleForUsers: settingsMap["radio_visible_for_users"] === "true",
        streakEnabled: settingsMap["streak_enabled"] === "true",
        streakVisibleForUsers: settingsMap["streak_visible_for_users"] === "true",
        streakShowWelcomeModal: settingsMap["streak_show_welcome_modal"] === "true",
        loginClockEnabled: settingsMap["login_clock_enabled"] !== "false",
        loginClockSize: (settingsMap["login_clock_size"] as ClockSize) || 'sm',
        dashboardClockEnabled: settingsMap["dashboard_clock_enabled"] !== "false",
        dashboardClockSize: (settingsMap["dashboard_clock_size"] as ClockSize) || 'md',
        adaptiveRecommendationsEnabled: settingsMap["adaptive_recommendations_enabled"] !== "false",
        showPostQuizSuggestions: settingsMap["show_post_quiz_suggestions"] !== "false",
        inactivityReminderDays: parseInt(settingsMap["inactivity_reminder_days"] || "3") || 3,
        shopEnabled: settingsMap["shop_enabled"] === "true",
        academyEnabled: settingsMap["academy_enabled"] === "true",
      });
    } catch (error) {
      console.error("Error fetching app settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateSetting = async (key: string, value: string | null) => {
    try {
      const { error } = await supabase
        .from("app_settings")
        .upsert(
          { setting_key: key, setting_value: value, updated_at: new Date().toISOString() },
          { onConflict: "setting_key" }
        );

      if (error) throw error;
      await fetchSettings();
      return { success: true };
    } catch (error) {
      console.error("Error updating setting:", error);
      return { success: false, error };
    }
  };

  return { settings, isLoading, updateSetting, refetch: fetchSettings };
};
